import { useRef, useState } from 'react';
import type { ApiClient } from '../api/client';

export type GoogleRegistration = { client_id: string; revision: number; updated_at: string };

export default function GoogleRegistrationSettings({ apiClient, callbackUrl, registration, configured, onUpdated }: {
  apiClient: ApiClient; callbackUrl: string; registration?: GoogleRegistration | null;
  configured: boolean; onUpdated: () => void;
}) {
  const [clientJson, setClientJson] = useState('');
  const [clientId, setClientId] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const readGeneration = useRef(0);
  const replacing = Boolean(registration && clientId && registration.client_id !== clientId);

  async function readFile(file?: File) {
    const generation = ++readGeneration.current;
    setClientJson(''); setClientId(''); setAcknowledged(false); setError('');
    if (!file) return;
    try {
      if (file.size > 32 * 1024) throw new Error();
      const json = await file.text();
      const value: unknown = JSON.parse(json);
      const web = (value as { web?: { client_id?: unknown; client_secret?: unknown } })?.web;
      if (typeof web?.client_id !== 'string' || !web.client_id.endsWith('.apps.googleusercontent.com') || typeof web.client_secret !== 'string' || !web.client_secret) throw new Error();
      if (generation === readGeneration.current) { setClientJson(json); setClientId(web.client_id); }
    } catch {
      if (generation === readGeneration.current) setError('Choose the downloaded Google web OAuth client JSON file (up to 32 KiB).');
    }
  }

  async function save() {
    if (inFlight.current || !clientJson || (replacing && !acknowledged) || (configured && !registration)) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      await apiClient.put('/api/google-owner/registration', {
        client_json: clientJson,
        expected_revision: registration?.revision ?? null,
        allow_client_replacement: replacing && acknowledged
      });
      setClientJson(''); setClientId(''); setAcknowledged(false);
      onUpdated();
    } catch {
      // Never render upstream errors that could echo uploaded credentials.
      setClientJson(''); setClientId(''); setAcknowledged(false);
      setError('Registration was not saved or its result could not be confirmed. Reload the connection status before trying again.');
    } finally { inFlight.current = false; setBusy(false); }
  }

  return <details className="card google-registration" open={!configured}>
    <summary><span><span className="google-registration-title">{configured ? 'Shared Google registration & rotation' : 'Set up Google once'}</span><span className="connection-help">{configured ? 'Manage the registration shared by all apps.' : 'An administrator completes this setup for all apps.'}</span></span></summary>
    <div className="google-registration-body">
    {!configured && <ol className="google-setup-steps">
      <li>Create a web OAuth client in Google Cloud.</li>
      <li>Enable Google Drive API and configure the consent screen.</li>
      <li>Add the callback below, then download and import the client JSON.</li>
    </ol>}
    <div className="connection-field"><span className="google-field-label">Authorized callback address</span>
      <div className="google-callback"><code>{callbackUrl}</code><button className="secondary" type="button" onClick={() => { void navigator.clipboard.writeText(callbackUrl).then(() => setCopied(true)).catch(() => setError('Select and copy the callback address above.')); }}>{copied ? 'Callback address copied' : 'Copy callback address'}</button></div>
    </div>
    {registration && <dl className="google-registration-meta"><div><dt>Client ID</dt><dd><code>{registration.client_id}</code></dd></div><div><dt>Last updated</dt><dd>{new Date(registration.updated_at).toLocaleString()}</dd></div></dl>}
    {configured && <p className="connection-notice">To rotate the secret, create a replacement for the same client in Google, then import its JSON here. Existing account connections stay connected. Verify a connection before retiring the old secret.</p>}
    <label className="connection-field">Google OAuth client JSON <input type="file" accept="application/json,.json" disabled={busy} onChange={event => { void readFile(event.target.files?.[0]); }} /></label>
    {clientId && <p className="connection-help">Selected client: <code>{clientId}</code></p>}
    {replacing && <label className="google-consent"><input type="checkbox" checked={acknowledged} disabled={busy} onChange={event => setAcknowledged(event.target.checked)} /><span>I understand that changing the client ID disconnects existing accounts. Their owners must connect again; saved Drive files remain.</span></label>}
    <div className="connection-footer google-actions"><button type="button" disabled={busy || !clientJson || (replacing && !acknowledged) || (configured && !registration)} onClick={() => { void save(); }}>{busy ? 'Saving…' : configured ? 'Save replacement registration' : 'Save Google registration'}</button></div>
    {error && <><p className="connection-notice connection-notice--error" role="alert">{error}</p><button className="secondary" type="button" disabled={busy} onClick={onUpdated}>Reload connection status</button></>}
    </div>
  </details>;
}
